"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

interface AnimatedCounterProps {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  decimals?: number;
}

// useLayoutEffect warns during SSR; fall back to useEffect on the server.
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function AnimatedCounter({
  end,
  duration = 2000,
  prefix = "",
  suffix = "",
  className = "",
  decimals = 0,
}: AnimatedCounterProps) {
  // Start at the final value so the server-rendered HTML — and anything that
  // never runs the animation (no JS, crawlers, reduced motion) — shows the real
  // figure instead of a zero. The count-up is a client-side enhancement.
  const [count, setCount] = useState(end);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const hasAnimatedRef = useRef(false);
  const ref = useRef<HTMLSpanElement>(null);

  // Runs after hydration but before paint, so resetting to 0 never flashes the
  // final value first.
  useIsomorphicLayoutEffect(() => {
    if (prefersReducedMotion()) return;
    if (typeof IntersectionObserver === "undefined") return;

    setCount(0);
    setShouldAnimate(true);
  }, []);

  const animateCount = useCallback(() => {
    // Take the start from the first frame's own timestamp. Mixing
    // performance.now() with the rAF clock is not safe — they need not share a
    // time origin, which can send progress negative on the first frame.
    let startTime: number | null = null;

    const updateCount = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;

      const elapsed = currentTime - startTime;
      const progress = Math.min(Math.max(elapsed / duration, 0), 1);
      // Easing function (ease-out cubic)
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);

      setCount(end * easeOutCubic);

      if (progress < 1) {
        requestAnimationFrame(updateCount);
      } else {
        // Land exactly on the target rather than an eased approximation.
        setCount(end);
      }
    };

    requestAnimationFrame(updateCount);
  }, [duration, end]);

  useEffect(() => {
    if (!shouldAnimate) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasAnimatedRef.current) {
            hasAnimatedRef.current = true;
            animateCount();
          }
        });
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [shouldAnimate, animateCount]);

  const formattedCount =
    decimals > 0
      ? count.toFixed(decimals)
      : Math.round(count).toLocaleString("de-CH");

  return (
    <span ref={ref} className={`counter-animate ${className}`}>
      {prefix}
      {formattedCount}
      {suffix}
    </span>
  );
}
